import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import LoanWrapper from '@sorare/core/src/components/card/Loan';
import { UnsealableOverlay } from '@sorare/core/src/components/card/UnsealableOverlay';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { BundledAuctionPreview } from 'components/auction/BundledAuctionPreview';
import { FlexCard } from 'components/token/FlexCard';
import { StackedToken } from 'components/token/StackedToken';
import { useIsBoostAffordable } from 'hooks/useIsBoostAffordable';
import { useIsCardDroppable } from 'hooks/useIsCardDroppable';
import { MyCardsSelectionMode } from 'lib/my-cards';

import { TokenImg_anyCard } from './__generated__/index.graphql';

const FlexCardWrapper = styled(ButtonBase)`
  width: 100%;
  position: relative;

  &.selected {
    & > * {
      opacity: 0.7;
    }
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 12px;
      background-blend-mode: hard-light;
    }
  }

  &.selected.drop::after {
    outline: 3px solid var(--c-rivals-loss);
    background: radial-gradient(
      154.46% 96.82% at 50% 3.18%,
      rgba(255, 78, 34, 0) 70%,
      var(--c-rivals-loss) 100%
    );
  }

  &.selected.boost:after {
    outline: 3px solid var(--c-rivals-friendlies);
    background: radial-gradient(
      154.46% 96.82% at 50% 3.18%,
      rgba(0, 255, 247, 0) 70%,
      var(--c-rivals-friendlies) 100%
    );
  }

  &.selected.sealing:after {
    outline: 3px solid var(--c-brand-600);
    background: radial-gradient(
      154.46% 96.82% at 50% 3.18%,
      rgba(0, 255, 247, 0) 70%,
      var(--c-brand-600) 100%
    );
  }

  &.disabled {
    opacity: 0.5;
  }
`;

type Props = {
  card: TokenImg_anyCard;
  stackedTokensCount?: number;
  isBundledAuction?: boolean;
  isDesktopLayout: boolean;
  onToggleCard?: (slug: string, options?: { xpNeeded?: number }) => void;
  selected?: boolean;
  selectionMode?: MyCardsSelectionMode;
  totalXpNeeded?: number;
  isDoublonOfSelectedCardSlug?: boolean;
};

export const TokenImg = ({
  card,
  stackedTokensCount,
  isBundledAuction,
  isDesktopLayout,
  onToggleCard,
  selected,
  selectionMode,
  totalXpNeeded,
  isDoublonOfSelectedCardSlug,
}: Props) => {
  const { sport } = useSportContext();
  const imageWidth = isDesktopLayout ? 320 : 160;

  const { isIosApp, canUseIosNativeCardPage } = useIsMobileApp();

  const isBoostAffordable = useIsBoostAffordable();
  const { isBoostable, xpNeeded, affordable, hasCooldown } = isBoostAffordable(
    card,
    totalXpNeeded
  );
  const isSealable = card.sealable;

  const isCardDroppable = useIsCardDroppable();
  const { isDroppable } = isCardDroppable(card);

  if (stackedTokensCount && stackedTokensCount > 1) {
    return (
      <StackedToken card={card} count={stackedTokensCount} width={imageWidth} />
    );
  }
  if (card.latestEnglishAuction && isBundledAuction) {
    return (
      <BundledAuctionPreview
        bundledAuction={card.latestEnglishAuction}
        displayOneCard={!isDesktopLayout}
      />
    );
  }

  const disabled =
    (selectionMode === 'boost' && !selected && !isBoostable) ||
    (selectionMode === 'drop' && !selected && !isDroppable) ||
    (selectionMode === 'sealing' && !selected && !isSealable) ||
    isDoublonOfSelectedCardSlug;

  const showUnsealableOverlay =
    selectionMode === 'sealing' &&
    ((!card.sealed && !card.sealable) || isDoublonOfSelectedCardSlug);
  return (
    <ConditionalWrapper
      Wrapper={UnsealableOverlay}
      wrap={Boolean(showUnsealableOverlay)}
      props={{ card, isDoublonOfSelectedCardSlug }}
    >
      <FlexCardWrapper
        className={classNames(selectionMode, {
          selected,
          disabled,
        })}
        onClick={() => {
          const canClickBoost =
            selectionMode === 'boost' &&
            (affordable || selected) &&
            !hasCooldown;
          const canClickDrop =
            selectionMode === 'drop' && (selected || isDroppable);
          const canClickSeal = selectionMode === 'sealing' && !disabled;

          if (canClickBoost || canClickDrop || canClickSeal) {
            onToggleCard?.(card.slug, { xpNeeded });
          }
        }}
      >
        <LoanWrapper card={card}>
          <FlexCard
            card={card}
            width={imageWidth}
            withLink={
              (canUseIosNativeCardPage || !isIosApp) &&
              (!selectionMode ||
                (sport !== Sport.FOOTBALL && sport !== Sport.BASEBALL))
            }
          />
        </LoanWrapper>
      </FlexCardWrapper>
    </ConditionalWrapper>
  );
};

TokenImg.fragments = {
  anyCard: gql`
    fragment TokenImg_anyCard on AnyCardInterface {
      slug
      sealed
      sealable
      latestEnglishAuction {
        id
        cards: anyCards {
          slug
        }
        ...BundledAuctionPreview_auction
      }
      ...FlexCard_anyCard
      ...StackedToken_anyCard
      ...useIsBoostAffordable_anyCard
      ...useIsCardDroppable_anyCard
      ...LoanWrapper_anyCard
      ...UnsealableOverlay_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${StackedToken.fragments.anyCard}
    ${BundledAuctionPreview.fragments.auction}
    ${useIsBoostAffordable.fragments.anyCard}
    ${useIsCardDroppable.fragments.anyCard}
    ${LoanWrapper.fragments.anyCard}
    ${UnsealableOverlay.fragments.anyCard}
  ` as TypedDocumentNode<TokenImg_anyCard>,
};
